import { apiCall, isBackendConfigured } from './api-call'
import {
  ApplicationParagraphsResult,
  CompareVerdictResult,
  DailyPepTalkResult,
  InquiryEmailResult,
  LeaseClauseCheckResult,
  ListingBreakdownResult,
  ListingsQuestionResult,
  RenterLlmCompletionKind,
  TagSuggestionResult,
  WeeklyDigestResult,
} from '../_types/quickpass-llm'

const offlineWarning = (operation: string): void => {
  console.warn(`[renter-llm] backend not configured — skipping ${operation}.`)
}

const safeParseJson = <Result>(raw: string): Result | null => {
  try {
    return JSON.parse(raw) as Result
  } catch (error) {
    console.error('[renter-llm] failed to parse LLM JSON', error, raw)
    return null
  }
}

const runRenterLlm = async <Result>(
  kind: RenterLlmCompletionKind,
  context: Record<string, unknown>,
): Promise<Result | null> => {
  if (isBackendConfigured() === false) {
    offlineWarning(kind)
    return null
  }

  const response = await apiCall({
    group: 'quickpass',
    command: 'runllmcompletion',
    data: { kind, context },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }

  return safeParseJson<Result>(response.data)
}

/**
 * Asks the backend LLM to generate a renter-tailored breakdown for a
 * single listing. Resolves to null when the backend isn't configured
 * (dev-bypass mode) — callers render an offline placeholder.
 *
 * @param {Record<string, unknown>} context - { listing, preferences, takeHome }
 * @returns {Promise<ListingBreakdownResult | null>} the parsed breakdown
 */
const requestListingBreakdown = async (
  context: Record<string, unknown>,
): Promise<ListingBreakdownResult | null> => {
  return runRenterLlm<ListingBreakdownResult>('listing-breakdown', context)
}

/**
 * Asks the backend LLM to recommend a tag (yes/maybe/no) for a listing.
 *
 * @param {Record<string, unknown>} context - { listing, preferences, takeHome }
 * @returns {Promise<TagSuggestionResult | null>} the suggestion + reasoning
 */
const requestTagSuggestion = async (
  context: Record<string, unknown>,
): Promise<TagSuggestionResult | null> => {
  return runRenterLlm<TagSuggestionResult>('tag-suggestion', context)
}

/**
 * Asks the backend LLM to draft an inquiry email for a listing tuned
 * to the renter's name + preferences.
 *
 * @param {Record<string, unknown>} context - { listing, renter, preferences }
 * @returns {Promise<InquiryEmailResult | null>} the subject + body draft
 */
const requestInquiryEmail = async (
  context: Record<string, unknown>,
): Promise<InquiryEmailResult | null> => {
  return runRenterLlm<InquiryEmailResult>('inquiry-email', context)
}

/**
 * Asks the backend LLM to generate the weekly decision-log digest from
 * a pre-computed activity summary.
 *
 * @param {Record<string, unknown>} context - the activity-window summary
 * @returns {Promise<WeeklyDigestResult | null>} the digest copy
 */
const requestWeeklyDigest = async (
  context: Record<string, unknown>,
): Promise<WeeklyDigestResult | null> => {
  return runRenterLlm<WeeklyDigestResult>('weekly-digest', context)
}

/**
 * Asks the backend LLM to answer a freeform question about the renter's
 * tracked listings. Returns the answer plus matched listing IDs.
 *
 * @param {Record<string, unknown>} context - { question, listings, preferences }
 * @returns {Promise<ListingsQuestionResult | null>} the answer + matched ids
 */
const requestListingsQuestion = async (
  context: Record<string, unknown>,
): Promise<ListingsQuestionResult | null> => {
  return runRenterLlm<ListingsQuestionResult>('listings-question', context)
}

/**
 * Asks the backend LLM to pick a winner between two listings for the
 * compare modal, with per-side pros + a swing-factor sentence.
 *
 * @param {Record<string, unknown>} context - { alpha, beta, preferences }
 * @returns {Promise<CompareVerdictResult | null>} the verdict
 */
const requestCompareVerdict = async (
  context: Record<string, unknown>,
): Promise<CompareVerdictResult | null> => {
  return runRenterLlm<CompareVerdictResult>('compare-verdict', context)
}

/**
 * Asks the backend LLM to write a personalised daily pep-talk for the
 * dashboard. Returns a greeting + body + microNudge.
 *
 * @param {Record<string, unknown>} context - { firstName, streakDays, snapshot, dateString }
 * @returns {Promise<DailyPepTalkResult | null>} the pep-talk copy
 */
const requestDailyPepTalk = async (
  context: Record<string, unknown>,
): Promise<DailyPepTalkResult | null> => {
  return runRenterLlm<DailyPepTalkResult>('daily-pep-talk', context)
}

/**
 * Asks the backend LLM to assess a pasted lease for unusual or risky
 * clauses, returning a severity-coded breakdown + risk score.
 *
 * @param {Record<string, unknown>} context - { leaseText, jurisdiction }
 * @returns {Promise<LeaseClauseCheckResult | null>} the clause breakdown
 */
const requestLeaseClauseCheck = async (
  context: Record<string, unknown>,
): Promise<LeaseClauseCheckResult | null> => {
  return runRenterLlm<LeaseClauseCheckResult>('lease-clause-check', context)
}

/**
 * Asks the backend LLM to draft the three durable paragraphs a renter
 * pastes into rental application forms. Profile-driven; truthful.
 *
 * @param {Record<string, unknown>} context - { profile, listingsContextSummary }
 * @returns {Promise<ApplicationParagraphsResult | null>} the paragraphs
 */
const requestApplicationParagraphs = async (
  context: Record<string, unknown>,
): Promise<ApplicationParagraphsResult | null> => {
  return runRenterLlm<ApplicationParagraphsResult>(
    'application-paragraphs',
    context,
  )
}

export {
  requestListingBreakdown,
  requestTagSuggestion,
  requestInquiryEmail,
  requestWeeklyDigest,
  requestListingsQuestion,
  requestCompareVerdict,
  requestDailyPepTalk,
  requestLeaseClauseCheck,
  requestApplicationParagraphs,
}
